/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { useState, type FormEvent } from "react";
import { MatrixError, type MatrixClient } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { FieldRow, InputField } from "../input/Input";
import { changeOwnPassword } from "./changePassword";

export function PasswordSettings({ client }: { client: MatrixClient }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError("");
    setSuccess(false);
    if (password !== confirm) {
      setError(t("instance.ui.password_mismatch"));
      return;
    }
    setBusy(true);
    try {
      await changeOwnPassword(client, current, password);
      setCurrent("");
      setPassword("");
      setConfirm("");
      setSuccess(true);
    } catch (cause) {
      setError(
        t(
          cause instanceof MatrixError &&
            [401, 403].includes(cause.httpStatus ?? 0)
            ? "instance.ui.password_verification_failed"
            : "instance.errors.request_failed",
        ),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      style={{
        width: "100%",
        maxWidth: 350,
        margin: "24px auto 0",
        paddingTop: 20,
        borderTop: "1px solid var(--app-border)",
      }}
    >
      <h3>{t("instance.ui.change_password")}</h3>
      <form onSubmit={(event) => void submit(event)}>
        <FieldRow>
          <InputField
            id="current-account-password"
            label={t("instance.ui.current_password")}
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <FieldRow>
          <InputField
            id="new-account-password"
            label={t("instance.ui.new_password")}
            type="password"
            autoComplete="new-password"
            minLength={10}
            maxLength={256}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        <FieldRow>
          <InputField
            id="confirm-account-password"
            label={t("instance.ui.confirm_password")}
            type="password"
            autoComplete="new-password"
            minLength={10}
            maxLength={256}
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            disabled={busy}
          />
        </FieldRow>
        {error && <p role="alert">{error}</p>}
        {success && <p role="status">{t("instance.ui.password_changed")}</p>}
        <button
          className="glass-button"
          style={{ padding: "10px 16px" }}
          type="submit"
          disabled={busy}
        >
          {t("instance.ui.change_password")}
        </button>
      </form>
    </section>
  );
}
